import { CopyIcon, CutleryIconWonWithBG } from 'app/components/SvgAssets';
import { useDuplicateServiceProfile } from 'app/hooks/requests/commerce';
import { useGetServiceProfiles } from 'app/hooks/services';
import { useGetProfile } from 'app/hooks/user';
import { COMMERCE } from 'app/utilities/roles';
import { privateRoutes } from 'app/utilities/routes';
import { ServiceProfile } from 'app/utilities/types/shared';
import arrowRight from 'assets/images/arrowForward.svg';
import DefaultImageNoContent from 'assets/images/cuate.svg';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ElrButton } from 'ui-components';

const RestaurantList = () => {
  const navigate = useNavigate();
  const {
    id: userId,
    accountInformation: { id: roleId },
  } = useGetProfile();
  const {
    fetchServiceProfiles,
    data: serviceProfiles,
    isLoading: isServiceProfilesLoading,
  } = useGetServiceProfiles();
  const {
    handleDuplicateServiceProfile,
    isLoading: isDuplicateServiceProfileLoading,
  } = useDuplicateServiceProfile();

  // Track which restaurant is being duplicated
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  useEffect(() => {
    fetchServiceProfiles({ userId, roleId, outletType: 'restaurant' });
  }, [fetchServiceProfiles, userId, roleId]);

  // Refresh the list after duplication completes
  useEffect(() => {
    if (!isDuplicateServiceProfileLoading && duplicatingId) {
      fetchServiceProfiles({ userId, roleId, outletType: 'restaurant' });
      setDuplicatingId(null);
    }
  }, [
    isDuplicateServiceProfileLoading,
    duplicatingId,
    fetchServiceProfiles,
    userId,
    roleId,
  ]);

  if (isServiceProfilesLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  }

  const handleAddRestaurant = () => {
    // Navigate to add restaurant page
    navigate(
      `/${COMMERCE}/${privateRoutes.ecommerce}/${privateRoutes.restaurantOnboarding}`
    );
  };

  const handleRestaurantClick = (id: string) => {
    // Navigate to restaurant details
    navigate(
      `/${COMMERCE}/${privateRoutes.ecommerce}/${privateRoutes.restaurant}/${id}/${privateRoutes.restaurantDashboard}`
    );
  };

  const handleDuplicateRestaurant = async (id: string) => {
    try {
      setDuplicatingId(id);
      await handleDuplicateServiceProfile({ roleId, serviceProfileId: id });
    } catch (error) {
      /** no op */
    }
  };

  return (
    <div className="bg-elr-gray flex flex-col pt-8 px-7 md:pt-3 md:px-20 min-h-screen">
      <section className="md:flex md:flex-row flex-col items-center pt-6 justify-between pb-8">
        <div className="bg-white rounded-lg p-8 mx-auto w-1/2 shadow-sm">
          <h1 className="text-xl mb-2 text-elr-black">List of Restaurants</h1>
          <p className="text-elr-black-300 text-lg mb-8">
            The restaurants you have added so far
          </p>

          <div className="space-y-4">
            {serviceProfiles.length ? (
              serviceProfiles.map((serviceProfile: ServiceProfile) => (
                <div
                  key={serviceProfile._id}
                  onClick={() => handleRestaurantClick(serviceProfile._id)}
                  className="flex items-center justify-between p-4 border-b border-elr-gray-100 cursor-pointer"
                >
                  <div className="flex items-center">
                    <div className="bg-elr-gray-100 w-14 h-14 rounded-full flex items-center justify-center mr-4">
                      <CutleryIconWonWithBG />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium">
                        {serviceProfile.name}
                      </h3>
                      <p className="text-elr-black-300">
                        Added{' '}
                        {DateTime.fromJSDate(
                          new Date(serviceProfile.createdAt)
                        ).toFormat('dd-MM-yyyy')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <button
                      className="flex items-center justify-center p-2 bg-elr-blue bg-opacity-10 rounded-md mr-4 hover:bg-opacity-20"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent triggering the parent onClick
                        handleDuplicateRestaurant(serviceProfile._id);
                      }}
                      type="button"
                      disabled={
                        isDuplicateServiceProfileLoading &&
                        duplicatingId === serviceProfile._id
                      }
                    >
                      {isDuplicateServiceProfileLoading &&
                      duplicatingId === serviceProfile._id ? (
                        <span className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-1" />
                      ) : (
                        <CopyIcon fill="#3B82F6" width="20" height="20" />
                      )}
                      <span className="ml-1 text-elr-blue text-sm">
                        {isDuplicateServiceProfileLoading &&
                        duplicatingId === serviceProfile._id
                          ? 'Duplicating...'
                          : 'Duplicate'}
                      </span>
                    </button>
                    <img src={arrowRight} alt="arrow-right" />
                  </div>
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center h-full">
                <img
                  src={DefaultImageNoContent}
                  alt="upload-products"
                  className="max-w-sm mx-auto"
                />
                <div>
                  <p className="text-center text-base w-7/12 mx-auto text-elr-black text-opacity-60">
                    No restaurants added yet.
                  </p>
                </div>
              </div>
            )}
          </div>

          <ElrButton
            onClick={handleAddRestaurant}
            text="Add New Restaurant"
            className="w-full mt-8 bg-elr-black text-white py-4 rounded-md flex items-center justify-center"
            disabled={isDuplicateServiceProfileLoading}
          />
        </div>
      </section>
    </div>
  );
};

export default RestaurantList;
